import { useState } from "react";
import { useTaskDevices, useTaskDeviceTotal, type TaskDeviceFilters } from "../api/tasks";
import { extractErrorMessage } from "../api/errors";
import type { TaskDevice, TaskDeviceStatus } from "../api/types";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { formatRange, totalPagesFor, useCursorPaging } from "../hooks/useCursorPaging";
import { DataTable } from "./DataTable";
import { StatusBadge } from "./StatusBadge";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;
// A filtered count reads the task's rows, so it refreshes far less often
// than the page while a rollout is live.
const FILTERED_TOTAL_REFRESH_MS = 30_000;

// The per-device statuses the backend stores on a task's device rows —
// not the task-level vocabulary (queued / in_progress / completed). Keyed
// by the type so a new status can't be left out of the filter.
const DEVICE_STATUS_LABELS: Record<TaskDeviceStatus, string> = {
  pending: "Pending",
  downloading: "Downloading",
  installing: "Installing",
  succeeded: "Succeeded",
  failed: "Failed",
  timed_out: "Timed out",
};
const DEVICE_STATUS_OPTIONS = (Object.keys(DEVICE_STATUS_LABELS) as TaskDeviceStatus[]).map((value) => ({
  value,
  label: DEVICE_STATUS_LABELS[value],
}));

const columns = [
  { key: "status", header: "Status", render: (row: TaskDevice) => <StatusBadge status={row.status} /> },
  { key: "imei", header: "Device IMEI" },
  {
    key: "startedAt",
    header: "Started",
    render: (row: TaskDevice) =>
      row.startedAt ? new Date(row.startedAt).toLocaleString() : <span className="text-gray-400 dark:text-riot-navy-400">-</span>,
  },
  {
    key: "completedAt",
    header: "Completed",
    render: (row: TaskDevice) =>
      row.completedAt ? new Date(row.completedAt).toLocaleString() : <span className="text-gray-400 dark:text-riot-navy-400">-</span>,
  },
  { key: "attempts", header: "Attempts" },
  {
    key: "errorMessage",
    header: "Message",
    render: (row: TaskDevice) => row.errorMessage || <span className="text-gray-400 dark:text-riot-navy-400">-</span>,
  },
];

interface TaskDeviceProgressTableProps {
  taskId: string;
  // The task's own device count. With no filter active it is the total,
  // so the table needn't count the task's rows on the server.
  totalDevices?: number;
  // Renders a heading with the total beside it.
  title?: string;
  // Poll while a rollout is live so progress moves without a reload.
  refetchInterval?: number;
}

// TaskDeviceProgressTable is a task's per-device progress, paged, filtered
// and counted on the server. A rollout can target thousands of devices, so
// filtering one loaded page in the browser would hide most of them.
export function TaskDeviceProgressTable({ taskId, totalDevices, title, refetchInterval }: TaskDeviceProgressTableProps) {
  const [status, setStatus] = useState<TaskDeviceStatus | "">("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const filters: TaskDeviceFilters = { status, search: debouncedSearch };
  const filtered = status !== "" || debouncedSearch.trim() !== "";
  const paging = useCursorPaging(JSON.stringify([taskId, status, debouncedSearch.trim(), pageSize]));

  const pageQuery = useTaskDevices(taskId, { limit: pageSize, cursor: paging.cursor, filters, refetchInterval });
  const useTaskRecordTotal = !filtered && totalDevices != null;
  const totalQuery = useTaskDeviceTotal(taskId, {
    filters,
    enabled: !useTaskRecordTotal,
    refetchInterval: refetchInterval ? FILTERED_TOTAL_REFRESH_MS : undefined,
  });

  const rows = pageQuery.data?.data ?? [];
  const hasMore = !!pageQuery.data?.pagination.hasMore;
  // A new filter's count may still be in flight: show "…", not the old one.
  const total = useTaskRecordTotal
    ? totalDevices
    : totalQuery.isPlaceholderData
      ? undefined
      : totalQuery.data?.total;
  const totalIsLowerBound = !useTaskRecordTotal && !totalQuery.isPlaceholderData && !!totalQuery.data?.isLowerBound;

  function next() {
    const cursor = pageQuery.data?.pagination.cursor;
    if (!cursor || pageQuery.isPlaceholderData) return;
    paging.next(cursor, rows.length);
  }

  // A sparse filter can use up the server's per-request budget before it
  // finds a match: that's an empty page with more to check, not "none".
  const emptyMessage =
    hasMore && rows.length === 0 && !pageQuery.isPlaceholderData ? (
      <span>
        No matches in the devices checked so far.{" "}
        <button
          type="button"
          onClick={next}
          className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400"
        >
          Keep searching
        </button>
      </span>
    ) : undefined;

  return (
    <div>
      {pageQuery.isError && (
        <p role="alert" className="mb-2 text-xs text-red-600 dark:text-red-400">
          Couldn&apos;t load device progress: {extractErrorMessage(pageQuery.error)}
        </p>
      )}
      <DataTable
        title={title}
        totalCount={total}
        columns={columns}
        data={rows}
        keyExtractor={(d) => d.imei}
        serverSide
        emptyMessage={emptyMessage}
        filters={{
          searchPlaceholder: "Search by IMEI",
          searchFields: [{ value: "imei", label: "IMEI" }],
          statusOptions: DEVICE_STATUS_OPTIONS,
        }}
        onFilterChange={(f) => {
          setStatus(f.status as TaskDeviceStatus | "");
          setSearch(f.search);
        }}
        pagination={{
          page: paging.page,
          totalPages: totalPagesFor(paging.page, pageSize, hasMore, total),
          count: rows.length,
          hasMore,
          rangeLabel: formatRange(paging.offset, rows.length, total, totalIsLowerBound),
          pageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageSizeChange: setPageSize,
          onNext: next,
          onPrev: paging.prev,
          onFirst: paging.first,
        }}
      />
    </div>
  );
}
